import AlignBoxMiddleCenter16 from '@carbon/icons-react/es/align-box--middle-center/16';
import ChevronDown20 from '@carbon/icons-react/es/chevron--down/20';
import Close16 from '@carbon/icons-react/es/close/16';
import Close20 from '@carbon/icons-react/es/close/20';
import { List, Space, Tooltip, Typography } from 'antd';
import { ListItemMetaProps } from 'antd/lib/list/Item';
import React, { FC, ReactElement, useEffect, memo } from 'react';
import { CurrencyFlagSizes } from 'react-currency-flags';
import { FormattedMessage } from 'react-intl';
import { components } from 'react-select';
import { VariableSizeList as VariableWindowList } from 'react-window';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Holiday } from 'api/holiday-calendar/types';
import { TimeoffResponse } from 'api/timeoff/types';
import HolidayCalendarIconSVG from 'assets/svgs/holiday-calendar-display.svg';
import TimeOffBoat from 'assets/svgs/timeoff-sailboat.svg';
import { HeightStyle } from 'components/atoms/input/input';
import { BodyShort01, SingleLineEllipsis } from 'components/styles';
import { useHolidayCalendar } from 'hooks/use-holiday-calendars';
import { getCurrencyLabel } from 'utils/currency';
import { getHolidayArray } from 'utils/holiday-calendar-utils';
import { getTimeOffArray } from 'utils/time-off-utils';
import { GetHolidayTooltipContent } from '../../molecules/holiday-tooltip';
import { Avatar } from '../avatar';
import { CurrencyFlagRenderer } from '../currency-renderer';
import { IconWrapper } from '../icon-shell/icon-shell';
import { Tag as TagAtom, TagProps } from '../tag';
import { GetTimeOffTooltipContent } from '../time-off-tooltip';
import { optionHeight } from './constants';
import {
  ControlProps,
  DropdownIndicatorProps,
  IndicatorProps,
  MultiValueProps,
  Option,
  OptionProps,
  SingleValueProps,
  SelectMode,
  Option as BaseOptionT,
} from './types';

const AdditionalInfo = styled.div`
  ${BodyShort01};
  color: var(--gray-50);
`;

const CurrencyOptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  flex: 1;
`;

const CurrencyOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const Tag = styled(TagAtom)`
  padding: 0 var(--spacing-02) 0 0;
`;

const GroupHeading = styled.div`
  color: var(--gray-60);
  cursor: default;
  display: block;
  font-size: var(--font-size-sm);
  font-weight: 500;
  margin-bottom: var(--spacing-02);
  padding: var(--spacing-04) var(--spacing-04) var(--spacing-02);
  text-transform: uppercase;
`;

type AvatarTagProps = TagProps & Pick<Option, 'imageUrl' | 'label' | 'logoUrl'>;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyleAvatar = styled(Avatar)`
  margin-right: var(--spacing-03);
  flex-shrink: 0;
`;

export const TimeOffWrapper = styled(Wrapper)`
  gap: var(--spacing-03);
  svg {
    color: var(--purple-60);
  }
`;

export const TextWrapper = styled.div`
  width: 98%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${BodyShort01};
  > span {
    color: inherit;
  }
`;

export const AvatarTag: FC<AvatarTagProps> = ({
  color = 'gray',
  imageUrl,
  label,
  logoUrl,
  ...restProps
}) => (
  <Tag {...restProps} color={color}>
    <Wrapper>
      <StyleAvatar
        size="small"
        src={imageUrl}
        companyLogoUrl={logoUrl}
        name={label}
      />
      <Typography.Text ellipsis title={label}>
        {label}
      </Typography.Text>
    </Wrapper>
  </Tag>
);

export const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDown20 />
    </components.DropdownIndicator>
  );
};

export const ClearIndicator = (props: IndicatorProps): JSX.Element => (
  <components.ClearIndicator {...props}>
    <Close20 />
  </components.ClearIndicator>
);

const StyledControl = styled(components.Control)<ControlProps>`
  outline-offset: -2px;
  outline: 2px solid !important;
  outline-color: transparent !important;
  border: 0;
  border-bottom: 1px solid var(--gray-50);
  border-radius: 0;
  background-color: var(--gray-10);
  min-height: ${HeightStyle};
  ${ifProp(
    'isFocused',
    css`
      box-shadow: none;
      border-bottom-color: transparent !important;
      outline-color: var(--primary-color) !important;
      outline-offset: -2px;
    `,
  )};
  &:hover {
    background-color: var(--hover-color);
  }
`;

export const Control = (props): JSX.Element => <StyledControl {...props} />;

export const TagOption = (props: OptionProps): JSX.Element => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <TagAtom color={data.color}>{data.label}</TagAtom>
    </components.Option>
  );
};

export const TagSingleValue = (props: SingleValueProps): JSX.Element => {
  const { data } = props;

  return (
    <components.SingleValue {...props}>
      <TagAtom color={data.color}>{data.label}</TagAtom>
    </components.SingleValue>
  );
};

export const MultiValueRemove = (props: MultiValueProps): JSX.Element => (
  <components.MultiValueRemove {...props}>
    <Close16 />
  </components.MultiValueRemove>
);

export const MultiValueLabel = (props: OptionProps) => {
  const { data: data1 } = props;
  const data = data1 as Option;

  return (
    <components.MultiValueLabel {...props}>
      <Space size="small">
        <Avatar
          size="small"
          src={data.imageUrl}
          name={data.__isNew__ ? data.value.toString() : data.label}
          companyLogoUrl={data.logoUrl}
        />
        <div>{data.label}</div>
      </Space>
    </components.MultiValueLabel>
  );
};

export const CurrencyValueLabel = (props: OptionProps) => {
  const { data: data1 } = props;
  const data = data1 as Option;

  return (
    <components.MultiValueLabel {...props}>
      <CurrencyOptionLabel>
        <CurrencyFlagRenderer
          currency={data.value as string}
          size={CurrencyFlagSizes.SMALL}
        />
        {getCurrencyLabel(data.extra)}
      </CurrencyOptionLabel>
    </components.MultiValueLabel>
  );
};

export const MultiValue = (props: MultiValueProps): JSX.Element => {
  const { data } = props;

  return (
    <components.MultiValue {...props}>
      <Tag color={data?.color || 'gray'} />
    </components.MultiValue>
  );
};

export const ListItem = styled(List.Item)`
  padding: 0;
  cursor: pointer;
`;

type MetaProps = ListItemMetaProps & {
  isFocused: boolean;
  isSelected: boolean;
};

export const ListItemMeta = styled(List.Item.Meta)<MetaProps>`
  align-items: center;
  & .ant-list-item-meta-title {
    margin-bottom: 0;
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100) !important;
          `
        : css`
            color: var(--gray-100);
          `};
  }
  & .ant-list-item-meta-description {
    font-size: var(--font-size-sm);
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100);
          `
        : css`
            color: var(--gray-100);
          `};
    font-weight: normal;
    ${SingleLineEllipsis};
  }
`;

export const TitleWrapper = ({
  title,
  timeOffText,
  timeOffArray,
  holidayText,
  holidayArray,
}) => {
  const { Text } = Typography;

  return (
    <TimeOffWrapper data-cy={`tag-option-${title}`}>
      <TextWrapper>
        <Text ellipsis={{ tooltip: true }}>{title}</Text>
      </TextWrapper>

      {!!timeOffArray && timeOffArray?.length > 0 && (
        <Tooltip title={timeOffText}>
          <img src={TimeOffBoat} alt="time-off" />
        </Tooltip>
      )}
      {!!holidayArray && holidayArray?.length > 0 && (
        <Tooltip title={holidayText}>
          <img src={HolidayCalendarIconSVG} alt="holiday" />
        </Tooltip>
      )}
    </TimeOffWrapper>
  );
};

export const AvatarOption = memo((props: OptionProps): JSX.Element => {
  const { data, isSelected, isFocused } = props;

  let timeOffArray: TimeoffResponse[] | undefined = [];
  let holidayArray: Holiday[] | undefined = [];

  const { holidayCalendar } = useHolidayCalendar(data?.holidayCalendarId);

  if (
    !!props.selectProps.startDate &&
    !!props.selectProps.dueDate &&
    !!data.userTimeOffDetails
  ) {
    timeOffArray = getTimeOffArray(
      props.selectProps.startDate,
      props.selectProps.dueDate,
      data.userTimeOffDetails,
    );
  }

  if (
    !!props.selectProps.startDate &&
    !!props.selectProps.dueDate &&
    !!holidayCalendar
  ) {
    const holidays = holidayCalendar?.holidays;

    holidayArray = getHolidayArray(
      props.selectProps.startDate,
      props.selectProps.dueDate,
      holidays,
    );
  }

  const timeOffText = (
    <GetTimeOffTooltipContent timeOff={timeOffArray} userName={data.label} />
  );
  const holidayText = <GetHolidayTooltipContent holiday={holidayArray} />;

  return (
    <components.Option {...props}>
      <ListItem data-cy="list-item-user">
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          avatar={
            <Avatar
              name={data.__isNew__ ? data.value : data.label}
              src={data.imageUrl}
              userStatus={data.userStatus}
              companyLogoUrl={data.logoUrl}
            />
          }
          title={
            <TitleWrapper
              title={data.label}
              timeOffText={timeOffText}
              timeOffArray={timeOffArray}
              holidayText={holidayText}
              holidayArray={holidayArray}
            />
          }
          description={data.description}
        />
      </ListItem>
    </components.Option>
  );
});

export const CompanyOption = (props: OptionProps): JSX.Element => {
  const { data, isSelected, isFocused } = props;

  return (
    <components.Option {...props}>
      <ListItem data-cy={`select.option.${data.value}`}>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          avatar={<Avatar name={data.label} src={data.extra.companyLogoUrl} />}
          title={data.label}
        />
      </ListItem>
    </components.Option>
  );
};

const { Paragraph: AntParagraph } = Typography;

export const SingleLineLabel = styled(AntParagraph).attrs({
  ellipsis: { rows: 1, tooltip: true },
})`
  margin-bottom: 0;
  color: inherit;
`;
const DescriptiveOptionColorStyles = css<{
  isSelected: boolean;
  isDisabled: boolean;
}>`
  ${({ isSelected }) =>
    isSelected &&
    css`
      color: var(--white-100) !important;
    `};

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      color: var(--gray-40) !important;
    `};
`;
const Icon = styled.div``;
const ListItemForDescriptiveOption = styled(ListItem)<{
  isSelected: boolean;
  isDisabled: boolean;
}>`
  gap: var(--spacing-05);
  ${Icon} {
    ${DescriptiveOptionColorStyles};
  }
  .ant-list-item-meta-title {
    ${BodyShort01};
    color: var(--gray-100);
    ${DescriptiveOptionColorStyles};
  }
  .ant-list-item-meta-description {
    color: var(--gray-70);
    ${({ isSelected }) =>
      isSelected &&
      css`
        color: var(--white-80);
      `};
    ${({ isDisabled }) =>
      isDisabled &&
      css`
        color: var(--gray-30);
      `};
    line-height: var(--line-height-helper-01);
    white-space: normal;
  }
`;
const ListItemMetaForDescriptiveOption = styled(ListItemMeta)<{
  isSelected: boolean;
  isDisabled: boolean;
}>`
  .ant-list-item-meta-title {
    ${BodyShort01};
    color: var(--gray-100);
    ${DescriptiveOptionColorStyles};
  }
  .ant-list-item-meta-description {
    color: var(--gray-70);
    ${({ isSelected }) =>
      isSelected &&
      css`
        color: var(--white-80);
      `};
    ${({ isDisabled }) =>
      isDisabled &&
      css`
        color: var(--gray-30);
      `};
    line-height: var(--line-height-helper-01);
    white-space: normal;
  }
`;

export const DescriptiveOption = memo((props: OptionProps): JSX.Element => {
  const { data, isSelected, isFocused } = props;

  return (
    <components.Option {...props}>
      <Tooltip
        title={
          data.isDisabled &&
          data.tooltipTextId && <FormattedMessage id={data.tooltipTextId} />
        }
        placement="right"
      >
        <ListItemForDescriptiveOption
          data-cy={`select.option.${data.value}`}
          isSelected={isSelected}
          isDisabled={data.isDisabled}
        >
          {data.icon != null ? <Icon>{data.icon}</Icon> : null}
          <ListItemMeta
            isFocused={isFocused}
            isSelected={isSelected}
            title={<SingleLineLabel>{data.label}</SingleLineLabel>}
            description={data.description}
          />
        </ListItemForDescriptiveOption>
      </Tooltip>
    </components.Option>
  );
});

export const SingleLineOption = memo((props: OptionProps): JSX.Element => {
  const { data, isSelected, isFocused } = props;

  return (
    <components.Option {...props}>
      <ListItem>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          avatar={
            <Avatar
              name={data.__isNew__ ? data.value : data.label}
              src={data.imageUrl}
              companyLogoUrl={data.logoUrl}
            />
          }
          title={<SingleLineLabel>{data.label}</SingleLineLabel>}
          description={data.description}
        />
      </ListItem>
    </components.Option>
  );
});

export const SingleValue = (props: SingleValueProps): JSX.Element => {
  const { data } = props;

  return (
    <components.SingleValue {...props}>
      <AvatarTag
        logoUrl={data.logoUrl}
        imageUrl={data.imageUrl}
        label={data.__isNew__ ? data.value.toString() : data.label}
      />
    </components.SingleValue>
  );
};

export const DescriptiveValue = memo((props: SingleValueProps): JSX.Element => {
  const { data } = props;

  return (
    <components.SingleValue {...props}>
      <ListItem data-cy={`select.option.${data.value}`}>
        <ListItemMetaForDescriptiveOption
          isFocused={false}
          isSelected={false}
          isDisabled={data.isDisabled}
          title={<SingleLineLabel>{data.label}</SingleLineLabel>}
          description={data.description}
        />
      </ListItem>
    </components.SingleValue>
  );
});

export const CurrencyOption = memo((props: OptionProps): JSX.Element => {
  const { data, isSelected, isFocused } = props;

  return (
    <components.Option {...props}>
      <ListItem data-cy={`select.option.${data.value}`}>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          title={
            <CurrencyOptionWrapper>
              <CurrencyOptionLabel>
                <CurrencyFlagRenderer
                  currency={data.value}
                  size={CurrencyFlagSizes.SMALL}
                />
                {getCurrencyLabel(data.extra, false)}
              </CurrencyOptionLabel>
              <AdditionalInfo>({data.extra.currencySymbol})</AdditionalInfo>
            </CurrencyOptionWrapper>
          }
        />
      </ListItem>
    </components.Option>
  );
});

// List Virtualization
const computeMenuItemHeight = (
  option: BaseOptionT & { type: 'groupHeading' | string },
  type: SelectMode,
) => {
  if (!option.label) {
    return 0;
  }
  if (option.type === 'groupHeading') {
    return optionHeight.groupHeading;
  }
  if (option.extra?.type === 'all-options') {
    return optionHeight.allOptions;
  }

  if (type === 'user') {
    return option.description
      ? optionHeight.userDescription
      : optionHeight.user;
  }
  if (type === 'tag') {
    return option.description ? optionHeight.tagDescription : optionHeight.tag;
  }

  return optionHeight.basic;
};

const flattenGroupedChildrens = (children: ReactElement[]) => {
  return children
    .map((child) => [
      {
        props: {
          data: {
            type: 'groupHeading',
            label: child.props?.data?.label,
          },
        },
      },
      ...child.props?.children,
    ])
    .reduce(
      (accumulator, currentValue) => accumulator.concat(currentValue),
      [],
    );
};

const RowRenderer = ({ index, style, data }) => {
  return (
    <div style={style}>
      {data[index]?.props?.data?.type === 'groupHeading' ? (
        <GroupHeading>{data[index]?.props?.data?.label}</GroupHeading>
      ) : (
        data[index]
      )}
    </div>
  );
};

const VariableListRowRenderer = (props) => {
  const {
    children,
    selectProps: { maxMenuHeight, grouped, type = 'basic', isMulti },
  } = props;

  const transformedChild = grouped
    ? flattenGroupedChildrens(children)
    : children;
  const itemCount = transformedChild.length;

  const listRef = React.useRef<VariableWindowList>(null);

  // Reset cache to make react window recalculate styles once the list of childrens are modified.
  const resetWindowedListCachePosition = () => {
    transformedChild.forEach((child, index) => {
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }
    });
  };

  useEffect(() => {
    resetWindowedListCachePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedChild.length]);

  return (
    <VariableWindowList
      height={maxMenuHeight}
      itemCount={isMulti ? itemCount + 1 : itemCount}
      itemData={transformedChild}
      ref={listRef}
      itemSize={(index: number) => {
        return computeMenuItemHeight(
          transformedChild[index]?.props?.data || [],
          type,
        );
      }}
      width="100%"
    >
      {RowRenderer}
    </VariableWindowList>
  );
};

export const VirtualizedMenuList = (props): JSX.Element => {
  const { children } = props;

  // For handling no options
  if (children && !Array.isArray(children)) {
    return children;
  }

  return <VariableListRowRenderer {...props} />;
};

export const FormOption = (props: OptionProps): JSX.Element => {
  const { data, isSelected, isFocused } = props;

  return (
    <components.Option {...props}>
      <ListItem>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          avatar={
            <IconWrapper>
              <AlignBoxMiddleCenter16 />
            </IconWrapper>
          }
          title={data.label}
        />
      </ListItem>
    </components.Option>
  );
};
